import moment from 'moment'
import { type ChangeEvent, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { type ReservationRequest } from '../../api/data/ReservationRequest'
import { datesTabVMMapper, type DatesTabVM } from '../../models/DatesTabVM'
import { getOEMName, getDealerNumber } from '../../modules/auth/helpers'
import { getConfig } from '../../services/config-service'
import { associatedVehiclesAction, selectVehicleAction } from '../../store/reservationSlice'
import type { RootState } from '../../store/store'
import './DatesTab.scss'
import ApiClient from '../../api/ReservationApi'
import configs from '../../configs/configs'
import { type RezResponse } from '../../api/data/RezResponse'
import { type AvailableVehicle } from '../../api/data/AvailableVehicle'
import { type ConfigAdvanceBookingHours } from '../../api/data/ConfigAdvanceBookingHours'
import { type TestDriveIntervals } from '../../models/TestDriveIntervals'

let updtReservation: ReservationRequest

const DatesTab = (): any => {
  const oemName = getOEMName()
  const dealerNumber = getDealerNumber()

  const [enabledRezScheduler, setEnableRezScheduler] = useState(false)
  const configValue = getConfig<boolean>('enableTestDriveRezScheduler', false)

  // default for an instant checkout date selection with 0 as buffer between current time and checkout dt.
  const fallbackAdvanceBookingHours: ConfigAdvanceBookingHours = { advanceBookingHours: 0 }

  const advanceBookingHoursConfig: ConfigAdvanceBookingHours =
    getConfig<ConfigAdvanceBookingHours>('tsdWebSchedulerAdvanceBookingHours', false) ?? fallbackAdvanceBookingHours

  const hoursBufferFromCurrentForCheckoutDtTm: number = advanceBookingHoursConfig.advanceBookingHours ?? 0

  const hoursOfOperationsAppSettings = getConfig<any>('hoursOfOperations', false)
  const webSchedulerTestDriveIntervals = getConfig<TestDriveIntervals[]>('webSchedulerTestDriveIntervals', false)?.filter(tdi => tdi.checked)

  useEffect(() => {
    setEnableRezScheduler(configValue ?? false)
    populateTimeIfTabSelectedAgain()
  }, [configValue])

  const dtNow = moment(new Date())
  const reservation = useSelector((state: RootState) => state.reservationSlice.reservation)
  const [customeError, setCustomeError] = useState<string>('')
  const [retnDtTmString, setRetnDtTmString] = useState<string>('')
  const [selectedInterval, setSelectedInterval] = useState<number>(0)
  const [testDriveLengthMessage, setTestDriveLengthMessage] = useState('')
  const [dropdownOptionsForCheckOut, setDropdownOptionsForCheckOut] = useState<string[]>([])

  let firstAvailableTime: any
  useEffect(() => {
    firstAvailableTime = dropdownOptionsForCheckOut?.[0]
    if (firstAvailableTime !== null && firstAvailableTime !== undefined) {
      setValue('checkoutTime', firstAvailableTime)
    }
  }, [dropdownOptionsForCheckOut])

  const dispatch = useDispatch()
  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<DatesTabVM>({
    mode: 'all',
    reValidateMode: 'onBlur',
    values: datesTabVMMapper(reservation)
  })

  const generateTimeOptions = (selectedDay: string, dropdownName: string, checkoutTimeLocal: string, returnTimeLocal: string): any => {
    const todaysDay = selectedDay === '' ? moment().format('dddd') : moment(selectedDay).format('dddd')
    const { OpenTime, CloseTime } = hoursOfOperationsAppSettings.HoursOfOperation.find(
      (singleobject: { DayLabel: string }) => singleobject.DayLabel === todaysDay
    )
    const startTime = getMinutesFromTimeString(OpenTime)
    const endTime = getMinutesFromTimeString(CloseTime)
    const options: string[] = []
    if (!Number.isNaN(startTime) && !Number.isNaN(endTime)) {
      for (let currentMinutes = startTime; currentMinutes <= endTime; currentMinutes += 30) {
        const hours = currentMinutes % 720 === 0 ? 12 : Math.floor(currentMinutes / 60) % 12
        const minutes = currentMinutes % 60
        const amPm = currentMinutes < 720 ? 'AM' : 'PM'
        const formattedTime = hours.toString().padStart(2, '0') + ':' + minutes.toString().padStart(2, '0') + ' ' + amPm
        options.push(formattedTime)
      }
    } else {
      options.push('Closed')
    }

    if (dropdownName === 'checkoutDate') {
      setDropdownOptionsForCheckOut(options)
      checkoutTimeLocal !== null && checkoutTimeLocal !== '' ? setValue('checkoutTime', checkoutTimeLocal) : setValue('checkoutTime', options[0])
    }
  }

  function populateTimeIfTabSelectedAgain(): any {
    if (getValues('checkoutDate') !== null && getValues('checkoutDate') !== undefined) {
      generateTimeOptions(getValues('checkoutDate'), 'checkoutDate', getValues('checkoutTime'), '')
      const revistPickup = moment(reservation?.pickupDateTime)
      const revistDropoff = moment(reservation?.dropOffDateTime)
      testDriveLengthHandleOnChange(revistDropoff.diff(revistPickup, 'hours'))
    }
  }
  const onSubmit = async (data: DatesTabVM): Promise<any> => {
    await handleNext(data)
  }
  async function getAvailabilityOnServer(resAvailbilityReq: ReservationRequest): Promise<RezResponse<AvailableVehicle> | undefined> {
    try {
      const api = new ApiClient(configs.REZ_URL)
      const url = `/api/Reservation/Partner/GenerateAvailability?dealerNumber=${dealerNumber}&oemName=${oemName}`
      const resResponse = await api.post<ReservationRequest, RezResponse<AvailableVehicle>>(url, resAvailbilityReq)
      return resResponse
    } catch (error: any) {}
  }
  function convertTime12to24(time12h: string): string {
    const [time, modifier] = time12h.split(' ')
    let [hours, minutes] = time.split(':')
    if (hours === '12') {
      hours = '00'
    }
    if (modifier === 'PM') {
      hours = (parseInt(hours, 10) + 12).toString()
    }

    return `${hours}:${minutes}`
  }
  async function handleNext(data: DatesTabVM): Promise<void> {
    // Clean up any previous error
    setCustomeError('')

    if (oemName === undefined || oemName.length === 0) {
      setCustomeError('OEM name is missing.')
      return
    }
    if (dealerNumber === undefined || dealerNumber.length === 0) {
      setCustomeError('Dealer number is missing. ')
      return
    }
    if (selectedInterval === undefined || selectedInterval === 0) {
      setTestDriveLengthMessage('Test Drive length is missing. ')
      return
    }
    const checkoutDtTmString = data.checkoutDate + ' ' + convertTime12to24(data.checkoutTime)
    const checkoutDtTm = new Date(checkoutDtTmString)
    const currentMoment = moment()
    const checkoutMoment = moment(checkoutDtTm)

    const diffCurrTmFromCheckoutDtTm = checkoutMoment.diff(currentMoment, 'minutes')

    const diffHoursCurrTmFromCheckoutDtTm = (diffCurrTmFromCheckoutDtTm > 0 ? diffCurrTmFromCheckoutDtTm : 1) / 60

    // Validate the date and times before calling the API for availability.
    if (diffHoursCurrTmFromCheckoutDtTm < hoursBufferFromCurrentForCheckoutDtTm) {
      setCustomeError(`Pick Up time should be after ${hoursBufferFromCurrentForCheckoutDtTm} hour(s) from current time. `)
      return
    }

    updtReservation = {
      ...reservation,
      pickupDateTime: checkoutDtTm,
      dropOffDateTime: new Date(retnDtTmString.replace('Return : ', '')),
      includeRates: true,
      oemName,
      dealerNumber,
      reservationType: 9,
      isReservation: true
    }
    const response = await getAvailabilityOnServer(updtReservation)
    let message: string | undefined = ''
    if (response?.statusCode !== 200) {
      message = response?.errorMessage
      const date = message?.split('.')?.[1]
      if (date != null) {
        message = message?.replace(date, updtReservation.pickupDateTime?.toLocaleString() ?? '') ?? message
      }
      setCustomeError(message ?? '')
      return
    }

    if (reservation?.pickupDateTime !== undefined && reservation?.pickupDateTime !== checkoutDtTm) {
      updtReservation = {
        ...updtReservation,
        rateCode: undefined,
        modelGroup: undefined,
        VIN: undefined,
        fees: []
      }
    }
    dispatch(associatedVehiclesAction({}))
    dispatch(selectVehicleAction(updtReservation))
  }

  if (!enabledRezScheduler) {
    return (
      <div className='mainMessage' data-testid='FinalTab'>
        <p>Please contact TSD Support to enable this location.</p>
      </div>
    )
  }
  function getMinutesFromTimeString(timeString: string): number {
    // Split the time string into hours, minutes, and period (AM/PM)
    const [time, period] = timeString.split(' ')
    const [hours, minutes] = time.split(':').map(Number)

    // Convert hours to 24-hour format if necessary
    let hours24 = hours
    if (period === 'PM' && hours !== 12) {
      hours24 = hours + 12
    } else if (period === 'AM' && hours === 12) {
      hours24 = 0
    }

    // Calculate the total minutes
    const totalMinutes = hours24 * 60 + minutes

    return totalMinutes
  }

  function dateInputHandleOnChange(e: ChangeEvent<HTMLInputElement>): void {
    setRetnDtTmString('')
    setTestDriveLengthMessage('')
    generateTimeOptions(e.target.value, e.target.id, '', '')
    const checkoutDate = e.target.value
    const checkoutTime = getValues('checkoutTime')

    if (checkoutDate !== null && checkoutDate !== undefined) {
      const chkoutDtTmString = checkoutDate + ' ' + checkoutTime
      if (selectedInterval > 0) {
        const retnDtTm = new Date(new Date(chkoutDtTmString).getTime() + selectedInterval * 60 * 60 * 1000)
        const retDtTmObject = moment(retnDtTm)
        const dayOrdays = (selectedInterval === 24) ? ' day' : ' days'
        if (retDtTmObject.isValid()) {
          setRetnDtTmString(
            'Return: ' +
            new Date(retnDtTm).toLocaleDateString() + ' ' +
            new Date(retnDtTm).toLocaleTimeString() +
            ' (' + String(selectedInterval / 24) + dayOrdays + ')'
          )
        } else {
          setRetnDtTmString('Please Select Pickup Date and Time')
        }
      }
    }
  }

  function checkoutTimeInputHandleOnChange(e: ChangeEvent<HTMLSelectElement>): void {
    setRetnDtTmString('')
    setTestDriveLengthMessage('')

    const checkoutTime = e.target.value
    setValue('checkoutTime', checkoutTime)

    if (getValues('checkoutDate') !== null && checkoutTime !== null) {
      updateReturnDate()
    }
  }

  function updateReturnDate(): void {
    const checkoutDate = getValues('checkoutDate')
    const checkoutTime = getValues('checkoutTime')
    if (checkoutDate !== null && checkoutTime !== null) {
      const chkoutDtTmString = getValues('checkoutDate') + ' ' + getValues('checkoutTime')

      if (selectedInterval > 0) {
        const retnDtTm = new Date(new Date(chkoutDtTmString).getTime() + selectedInterval * 60 * 60 * 1000)
        const retDtTmObject = moment(retnDtTm)
        const dayOrdays = (selectedInterval === 24) ? ' day' : ' days'

        if (retDtTmObject.isValid()) {
          setRetnDtTmString(
            'Return: ' +
            new Date(retnDtTm).toLocaleDateString() + ' ' +
            new Date(retnDtTm).toLocaleTimeString() +
            ' (' + String(selectedInterval / 24) + dayOrdays + ')'
          )
        } else {
          setRetnDtTmString('Please Select Pickup Date and Time')
        }
      }
    }
  }

  function testDriveLengthHandleOnChange(hrs: number): void {
    setSelectedInterval(hrs)
    setTestDriveLengthMessage('')
    if (getValues('checkoutDate') !== null && getValues('checkoutDate') !== undefined) {
      const chkoutDtTmString = getValues('checkoutDate') + ' ' + getValues('checkoutTime')
      const retnDtTm = new Date(new Date(chkoutDtTmString).getTime() + hrs * 60 * 60 * 1000)
      const retDtTmObject = moment(retnDtTm)
      const dayOrdays = (hrs === 24) ? ' day' : ' days'
      if (retDtTmObject.isValid()) {
        setRetnDtTmString('Return : ' +
            new Date(retnDtTm).toLocaleDateString() + ' ' +
            new Date(retnDtTm).toLocaleTimeString() +
            ' (' + String(hrs / 24) + dayOrdays + ')')
      } else {
        setRetnDtTmString('Please Select Pickup Date and Time')
      }
    }
  }

  return (
    <div className='tabs-panel is-active' id='panel1' role='tabpanel' aria-labelledby='panel1-label'>
      {customeError.length !== 0 ? <p className='errorMessage'>{customeError}</p> : ''}
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <div className='grid-x grid-margin-x'>
          <div className='cell small-6'>
            <legend>Extended Test Drive</legend>
          </div>
        </div>
        <div className='grid-x grid-margin-x'>
          <div className='cell small-12 medium-6'>
            <fieldset className='fieldset checkout'>
              <legend>
                <span className='material-symbols-outlined'>today</span> Pick Up
              </legend>
              <div className='grid-x grid-margin-x'>
                <div className='cell small-6'>
                  <label htmlFor='checkoutDate' className='required'>
                    Pick Up Date
                  </label>
                  <input
                    type='date'
                    id='checkoutDate'
                    disabled={isSubmitting}
                    min={dtNow.format('YYYY-MM-DD')}
                    className={errors.checkoutDate?.message !== undefined ? 'is-invalid-input' : ''}
                    {...register('checkoutDate', {
                      required: 'Pick Up Date is required',
                      validate: (value) => {
                        // Clean up any previous custom error
                        setCustomeError('')
                        const messageSuffix = 'Pickup'
                        const aCheckOutTime = getValues('checkoutTime')
                        if (aCheckOutTime === 'Closed') {
                          return `You have selected ${messageSuffix} date on which Location is Closed`
                        } else if (convertTime12to24(aCheckOutTime) !== undefined) {
                          const checkoutDateTimeString = value + ' ' + convertTime12to24(getValues('checkoutTime'))
                          const checkoutDateTime = new Date(checkoutDateTimeString)
                          const closedDays = hoursOfOperationsAppSettings.ClosedDates.map((d: string) => new Date(d))

                          const findarrayvalue: Date | undefined = closedDays.find((element: Date) => checkoutDateTime.getDate() === element.getDate() &&
                            checkoutDateTime.getMonth() === element.getMonth() && checkoutDateTime.getFullYear() === element.getFullYear())
                          return findarrayvalue !== undefined ? `You have selected ${messageSuffix} date on which Location is Closed` : true
                        } else {
                          return true
                        }
                      }
                    })}
                    onChange={(e) => {
                      dateInputHandleOnChange(e)
                    }}
                  />
                  <span className='form-error is-visible'>{errors.checkoutDate?.message}</span>
                </div>
                <div className='cell small-6'>
                  <label htmlFor='checkoutTime' className='required'>
                    Pick Up Time
                  </label>
                  <select id='checkoutTime' {...register('checkoutTime')} disabled={isSubmitting} onChange={(e) => { checkoutTimeInputHandleOnChange(e) }}>
                    {dropdownOptionsForCheckOut?.map((option, index) => (
                      <option key={index} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </fieldset>
          </div>
          <div className='cell small-12 medium-6'>
            <fieldset className='fieldset return'>
              <legend>
                <span className='material-symbols-outlined'>event</span> Return
              </legend>
              <div className='grid-x grid-margin-x'>
                <div className='cell small-12'>
                  <label htmlFor='testDriveLength' className='required'>
                    Test Drive Length
                  </label>
                  <label>{((webSchedulerTestDriveIntervals ?? '') !== '' &&
                   webSchedulerTestDriveIntervals?.length !== 0)
                    ? ''
                    : 'Intervals have not been setup.'}</label>
                  {webSchedulerTestDriveIntervals?.map((option, index) => (
                    <a
                      key={index}
                      className={parseInt(String(option.value), 0) === parseInt(String(selectedInterval), 0) ? 'button' : 'button hollow'}
                      onClick={() => {
                        testDriveLengthHandleOnChange(parseInt(String(option.value), 0))
                      }}
                    >
                      {' '}
                      {option.value} hrs.{' '}
                    </a>
                  ))}
                  <span className='form-error is-visible'>{testDriveLengthMessage}</span>
                </div>
              </div>
              <div className='grid-x grid-margin-x'>
                <div className='cell small-12'>
                  <label>
                    <span>{retnDtTmString}</span>
                  </label>
                </div>
              </div>
            </fieldset>
          </div>
        </div>
        <button disabled={isSubmitting} className='button primary float-right expanded submit' type='submit'>
          NEXT
        </button>
      </form>
    </div>
  )
}
export default DatesTab
